import { doc, getDoc } from "firebase/firestore";
import { createSignal } from "solid-js";
import { createQuery } from "@tanstack/solid-query";
import { firebase } from "@core/firebase/app";
import { queryKeys } from "@core/query";
import { statsService } from "@core/services-v2";
import { browserNotificationService } from "@core/services/BrowserNotificationService";

export type NotificationCounters = {
  total: number;
  bookings: number;
  robots: number;
  users: number;
  chats: number;
};

export type DashboardStats = {
  notifications: NotificationCounters;
};

const DASHBOARD_STATS_REF = doc(firebase.firestore, "stats", "dashboard");

const DEFAULT_NOTIFICATION_COUNTERS: NotificationCounters = {
  total: 0,
  bookings: 0,
  robots: 0,
  users: 0,
  chats: 0,
};

const [rawDashboardDoc, setRawDashboardDoc] = createSignal<Record<string, unknown> | null>(null);
const [dashboardStatsState, setDashboardStatsState] = createSignal<DashboardStats | null>(null);
let dashboardStatsUnsubscribe: (() => void) | null = null;

// Track previous notification counts to detect increases
let previousNotificationCounters: NotificationCounters | null = null;

const coerceNumber = (value: unknown): number => {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
};

const parseDashboardDoc = (raw: Record<string, unknown> | null): DashboardStats => {
  const typed = { ...(raw ?? {}) } as Record<string, unknown>;
  if ("id" in typed) {
    delete (typed as { id?: unknown }).id;
  }

  const authUid = firebase.auth.currentUser?.uid ?? null;

  const notificationsRaw =
    typeof typed.notificationCounters === "object" && typed.notificationCounters !== null
      ? (typed.notificationCounters as Record<string, unknown>)
      : null;

  const notifications = (() => {
    if (!authUid || !notificationsRaw) {
      return DEFAULT_NOTIFICATION_COUNTERS;
    }
    const entryRaw = notificationsRaw[authUid];
    if (typeof entryRaw !== "object" || entryRaw === null) {
      return DEFAULT_NOTIFICATION_COUNTERS;
    }
    const entry = entryRaw as Record<string, unknown>;
    return {
      total: coerceNumber(entry.total),
      bookings: coerceNumber(entry.bookings),
      robots: coerceNumber(entry.robots),
      users: coerceNumber(entry.users),
      chats: coerceNumber(entry.chats),
    };
  })();

  return {
    notifications,
  };
};

const applyDashboardSnapshot = (raw: Record<string, unknown> | null) => {
  setRawDashboardDoc(raw);
  const newStats = parseDashboardDoc(raw);
  setDashboardStatsState(newStats);

  // Fire browser notifications when counters increase
  if (previousNotificationCounters) {
    const prev = previousNotificationCounters;
    const curr = newStats.notifications;

    // Notify for each counter type that increased
    if (curr.bookings > prev.bookings) {
      const newCount = curr.bookings - prev.bookings;
      browserNotificationService.notifyNewBookings(newCount);
    }

    if (curr.chats > prev.chats) {
      const newCount = curr.chats - prev.chats;
      browserNotificationService.notifyNewChats(newCount);
    }

    if (curr.users > prev.users) {
      const newCount = curr.users - prev.users;
      browserNotificationService.notifyNewUsers(newCount);
    }

    if (curr.robots > prev.robots) {
      const newCount = curr.robots - prev.robots;
      browserNotificationService.notifyRobotAlerts(newCount);
    }
  }

  // Update previous counters for next comparison
  previousNotificationCounters = { ...newStats.notifications };
};

const ensureDashboardSubscription = () => {
  if (dashboardStatsUnsubscribe) {
    return;
  }
  dashboardStatsUnsubscribe = statsService.subscribeToDashboardStats((doc) => {
    applyDashboardSnapshot(doc);
  });
};

export const initializeDashboardStatsSubscription = () => {
  ensureDashboardSubscription();
};

export const cleanupDashboardStatsSubscription = () => {
  if (dashboardStatsUnsubscribe) {
    dashboardStatsUnsubscribe();
    dashboardStatsUnsubscribe = null;
  }
  // Reset previous counters on cleanup
  previousNotificationCounters = null;
};

export const dashboardStats = () => dashboardStatsState();

firebase.auth.onAuthStateChanged(() => {
  const raw = rawDashboardDoc();
  if (raw) {
    setDashboardStatsState(parseDashboardDoc(raw));
  }
});

export const fetchDashboardStats = async (): Promise<DashboardStats> => {
  ensureDashboardSubscription();

  const liveStats = dashboardStatsState();
  if (liveStats) {
    return liveStats;
  }

  const snapshot = await getDoc(DASHBOARD_STATS_REF);
  const data = snapshot.exists() ? (snapshot.data() as Record<string, unknown>) : {};
  applyDashboardSnapshot(data);
  return dashboardStatsState() ?? parseDashboardDoc(data);
};

export const useDashboardStatsQuery = () =>
  createQuery(() => ({
    queryKey: queryKeys.stats.dashboard(),
    queryFn: fetchDashboardStats,
  }));
